import { Box, Button, Grid2, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { useGetNetworkTokensQuery } from 'src/store/services/network.service';
import { useAppSelector } from 'src/store/utils';
import { isAllowedGreadterThanZero } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';

export function WhitelistPopupNotification({ isDisabled = false }: { isDisabled?: boolean }) {
  const { networkInfo } = useAppSelector(state => state.app);
  const {
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ProjectInputs>();
  const watchSetting = watch('setting');
  const { hasDeployed } = useDeployment();
  const { data: networkTokens } = useGetNetworkTokensQuery(
    { networkId: networkInfo?.id || '' },
    { skip: !networkInfo?.id },
  );

  useEffect(() => {
    if (!watchSetting?.defaultToken && networkTokens?.[0]?.symbol) {
      setValue('setting.defaultToken', networkTokens[0].symbol);
    }
  }, [networkTokens]);

  return (
    <Box width={'100%'}>
      <Box
        paddingTop={3}
        sx={{
          width: '100%',
        }}>
        <Typography paddingBottom={2} color={'white'} fontSize={'16px'} fontWeight={500} variant='h6'>
          Lock transfer
        </Typography>
        <Typography paddingBottom={3} color={'white'} fontSize={'14px'} fontWeight={400} variant='h6'>
          Determine the NFT lockup period (in days) after the start of the NFT sale. Users can transfer the NFT
          afterwards.
        </Typography>
        <Controller
          name='setting.lockTransfer'
          control={control}
          render={({ field }) => (
            <AdminNumberFormat
              isAllowed={isAllowedGreadterThanZero}
              onValueChange={values => {
                field.onChange(Number(values.floatValue) || 0);
              }}
              isError={!!errors.setting?.lockTransfer}
              errorMsg={errors.setting?.lockTransfer?.message}
              placeholder={'0'}
              suffix=''
              prefix=''
              readOnly={isDisabled || hasDeployed}
              {...field}
            />
          )}
        />
      </Box>
      <Box
        paddingTop={3}
        sx={{
          width: '100%',
        }}>
        <Typography paddingBottom={3} color={'white'} fontSize={'16px'} fontWeight={500} variant='h6'>
          Purchase Limits
        </Typography>
        <Grid2 container spacing={2}>
          <Grid2 size={6}>
            <Box
              sx={{
                width: '100%',
              }}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Per Wallet
              </Typography>
              <Controller
                name='setting.maxPurchaseWl'
                control={control}
                render={({ field }) => (
                  <AdminNumberFormat
                    decimalScale={0}
                    isAllowed={isAllowedGreadterThanZero}
                    onValueChange={values => {
                      field.onChange(Number(values.floatValue) || 0);
                    }}
                    isError={!!errors?.setting?.maxPurchaseWl}
                    errorMsg={errors?.setting?.maxPurchaseWl?.message}
                    placeholder={'0'}
                    suffix=''
                    prefix=''
                    readOnly={isDisabled}
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid2>
          <Grid2 size={6}>
            <Box
              sx={{
                width: '100%',
              }}>
              <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                Per Transaction
              </Typography>
              <Controller
                name='setting.maxPurchaseTx'
                control={control}
                render={({ field }) => (
                  <AdminNumberFormat
                    decimalScale={0}
                    isAllowed={isAllowedGreadterThanZero}
                    onValueChange={values => {
                      field.onChange(Number(values.floatValue) || 0);
                    }}
                    isError={!!errors?.setting?.maxPurchaseTx}
                    errorMsg={errors?.setting?.maxPurchaseTx?.message}
                    placeholder={'0'}
                    suffix=''
                    prefix=''
                    readOnly={isDisabled}
                    {...field}
                  />
                )}
              />
            </Box>
          </Grid2>
        </Grid2>
      </Box>
      <Box
        paddingTop={3}
        sx={{
          width: '100%',
        }}>
        <Typography paddingBottom={3} color={'white'} fontSize={'16px'} fontWeight={500} variant='h6'>
          Currency Selection
        </Typography>
        <Typography paddingBottom={3} color={'white'} fontSize={'14px'} fontWeight={400} variant='h6'>
          Choose your preferred currency. It will be the default on your collection sale page.
        </Typography>
        <Grid2 container spacing={2}>
          {networkTokens &&
            networkTokens.map((item, index) => {
              const isSelect = watchSetting.defaultToken === item.symbol;
              return (
                <Grid2 size={3} key={item.name}>
                  <Button
                    key={index}
                    variant='outlined'
                    color='primary'
                    onClick={() => {
                      if (isDisabled) return false;
                      setValue('setting.defaultToken', item.symbol);
                    }}
                    sx={{
                      width: '100%',
                      color: 'white',
                      borderColor: isSelect ? 'transparent' : `1px solid ${colors.neutral015}`,
                      borderRadius: '4px',
                      backgroundColor: isSelect ? colors.brand300 : 'transparent',
                      padding: '8px 16px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '16px',
                    }}>
                    <img width={24} height={24} src={item.image} alt={item.name} />
                    {item.name?.toLocaleUpperCase()}
                  </Button>
                </Grid2>
              );
            })}
        </Grid2>
      </Box>
    </Box>
  );
}
